/** @jsx jsx */
import { type CSSObject, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

import { AIGenerateBrandedIcon, CloseIcon } from '@coursera/cds-icons';

import QuickAccessBody from 'bundles/ai-coach-platform/components/patterns/popover/components/QuickAccessBody';
import QuickAccessButton from 'bundles/ai-coach-platform/components/patterns/popover/components/QuickAccessButton';
import QuickAccessDropdown from 'bundles/ai-coach-platform/components/patterns/popover/components/QuickAccessDropdown';
import { COACH_LABEL } from 'bundles/ai-coach-platform/components/patterns/popover/constants';
import { QuickAccessContext } from 'bundles/ai-coach-platform/components/patterns/popover/context/quickAccessContext';
import { usePopover } from 'bundles/ai-coach-platform/components/patterns/popover/hooks/usePopover';
import type { Action, Position } from 'bundles/ai-coach-platform/components/patterns/popover/types';

import _t from 'i18n!nls/ai-coach-platform';

type Props = {
  /**
   * Callback for when the user sends a message
   */
  onSend: (value: string) => void;

  /**
   * Position of the dropdown (default is bottom-start)
   */
  position?: Position;

  /**
   * Quick actions to render in the dropdown (optional)
   */
  actions?: Action[];

  /**
   * Whether the button trigger is disabled
   */
  disabled?: boolean;

  /**
   * Placeholder for the rich input field (optional)
   */
  placeholder?: string;

  /**
   * CSS styles overrides
   */
  className?: string;

  /**
   * Tooltip for the button trigger
   */
  tooltip?: string;

  /**
   * Callback for when the open state changes
   */
  onOpenChange?: (open: boolean) => void;

  /**
   * Custom body content to render when the popover is open
   */
  children?: (props: { handleClose: () => void }) => React.ReactNode;

  /**
   * Additional styles to apply to the QuickAccessDropdown
   */
  additionalDropdownStyles?: CSSObject;
};

/**
 * The Quick-access popover pattern consists of a CTA that triggers a popover with action buttons and a rich input field.
 * Use this pattern when you need a quick way to trigger Coach or send Coach message from outside the chat UI. The actual
 * trigger logic would be implemented by the team using callbacks, this pattern only provides the UI pattern.
 */
const QuickAccessPopover = ({
  actions,
  disabled,
  placeholder,
  position = 'bottom-start',
  className,
  onSend,
  tooltip,
  onOpenChange,
  children,
  additionalDropdownStyles,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');

  const handleClose = useCallback<() => void>(() => {
    setOpen(false);
  }, []);

  const handleSend = useCallback<(sendValue: string) => void>(
    (sendValue) => {
      onSend(sendValue);
      setValue('');
      setOpen(false);
    },
    [onSend, setOpen]
  );

  useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  const context = usePopover({ open, position, onOpenChange: setOpen });
  const ariaLabel = _t('#{coachLabel} short-cuts', { coachLabel: COACH_LABEL });
  const buttonTooltip = tooltip ?? _t('Chat with #{coachLabel}', { coachLabel: COACH_LABEL });

  return (
    <QuickAccessContext.Provider value={context}>
      <QuickAccessButton
        icon={open ? <CloseIcon /> : <AIGenerateBrandedIcon />}
        selected={open}
        disabled={disabled}
        aria-label={ariaLabel}
        tooltip={open ? _t('Close') : buttonTooltip}
        className={className}
      />

      <QuickAccessDropdown open={open} onClose={handleClose} additionalStyles={additionalDropdownStyles}>
        {children ? (
          children({ handleClose })
        ) : (
          <QuickAccessBody
            value={value}
            actions={actions}
            placeholder={placeholder}
            onSend={handleSend}
            onChange={setValue}
            onClose={handleClose}
          />
        )}
      </QuickAccessDropdown>
    </QuickAccessContext.Provider>
  );
};

export default QuickAccessPopover;
